<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <div class="d-flex justify-content-between align-items-center">
        <h1 class="page-title">年間予定作業 - 建物情報一覧</h1>
        <router-link :to="{ name: 'Home' }"> <!-- 業者情報詳細ページへのリンク -->
          <button type="button" class="btn btn-primary mx-1">ホーム</button>
        </router-link>
      </div>

      <!-- 検索バーの追加 -->
      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value" >
        <form class="search-container" style="background-color: #f8f9fa;" @submit.prevent="searchBuildings"> <!-- 背景色を薄く設定 -->
          <div class="row g-3 align-items-center">

            <!-- 顧客検索 -->
            <div class="col-auto">
              <CustomerSelectBox
                v-model="searchCustomerName"
                :options="customers"
                placeholder="顧客名で検索"
                :formatter="c => c.customer_nm"
              />
            </div>

            <!-- 建物種別検索 -->
            <div class="col-auto">
              <BuildingTypeSelectBox
                v-model="searchBuildingTypeName"
                :options="buildingTypes"
                placeholder="建物種別で検索"
                :formatter="bt => bt.building_type_nm"
              />
            </div>
            
            <!-- 建物名検索 -->
            <div class="col-auto">
              <input type="text" id="searchBuildingName" class="form-control" v-model="searchBuildingName" placeholder="建物名で検索">
            </div>

            <!-- 社内担当者検索 -->
            <div class="col-auto">
              <UserSelectBox
                v-model="searchInchargePersonName"
                :options="users"
                placeholder="社内担当者で検索"
                :formatter="v => v.name"
              />
            </div>

            <div class="col-auto">
              <button type="submit" class="btn btn-primary mx-1">検索</button>
              <button type="button" class="btn btn-secondary mx-1" @click="searchResetBuildings">クリア</button>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div v-if="buildings">
      <div class="table-wrapper" ref="tableWrapper">
        <table class="master-table table table-bordered">
          <thead class="table-dark">
            <tr>
              <th class="text-nowrap">顧客名</th>
              <th class="text-nowrap">建物名</th>
              <th class="text-nowrap">区分</th>
              <th class="text-nowrap">所在地</th>
              <th class="text-nowrap">契約月</th>
              <th class="text-nowrap">定額請求</th>
              <th class="text-nowrap">社内担当者</th>
              <th class="text-nowrap">契約終了日</th>
              <th class="btn-cell"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(group, customerName) in groupedBuildings" :key="customerName">
              <tr v-for="(building, idx) in group" :key="building.id">
                <td v-if="idx === 0" :rowspan="group.length" class="multiline-text">
                  {{ customerName }}
                </td>
                <td>{{ building.building_nm }}</td>
                <td class="text-nowrap">{{ building.m_building_type?.building_type_nm }}</td>
                <!-- 顧客名セル（1回だけ出す） -->
                <td>〒{{ building.postal_code }} {{building.prefecture}}{{building.city}}{{building.other_address}}</td>
                <td class="text-nowrap"><span v-if="building.contract_dt">{{ new Date(building.contract_dt).getMonth() + 1 }}月</span></td>
                <td>
                  {{
                    {
                      true: "対象",
                      false: "",
                    }[building.fixed_fee_billing]
                  }}
                </td>
                <td class="text-nowrap">{{ building.internal_person?.name }}</td>
                <td class="text-nowrap">{{ building.contract_end_dt }}</td>
                <td class="btn-cell">
                  <!-- 昨年度ボタン -->
                  <router-link
                    v-if="prevFiscal(building.contract_dt)"
                    :to="{ name: 'AnnualWorkScheduleList', params: { building_id: building.id, start_month: prevFiscal(building.contract_dt).start }, query: getSearchQuery() }">
                    <button type="button" class="btn btn-sm btn-success mx-1 my-1">
                      {{ prevFiscal(building.contract_dt).label }}
                    </button>
                  </router-link>

                  <!-- 今年度ボタン -->
                  <router-link
                    v-if="currentFiscal(building.contract_dt)"
                    :to="{ name: 'AnnualWorkScheduleList', params: { building_id: building.id, start_month: currentFiscal(building.contract_dt).start }, query: getSearchQuery() }">
                    <button type="button" class="btn btn-sm btn-warning mx-1 my-1">
                      {{ currentFiscal(building.contract_dt).label }}
                    </button>
                  </router-link>

                  <!-- 来年度ボタン -->
                  <router-link
                    v-if="nextFiscal(building.contract_dt)"
                    :to="{ name: 'AnnualWorkScheduleList', params: { building_id: building.id, start_month: nextFiscal(building.contract_dt).start }, query: getSearchQuery() }">
                    <button type="button" class="btn btn-sm btn-info mx-1 my-1">
                      {{ nextFiscal(building.contract_dt).label }}
                    </button>
                  </router-link>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <div v-else>
      建物情報情報を読み込み中...
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  getAnnualScheduleBuildings,
  getSelectBoxCustomers,
  getBuildingTypes,
  getSelectBoxUsers
} from '@/api/axios';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue'
import BuildingTypeSelectBox from '@/components/searchSelectBoxes/BuildingTypeSelectBox.vue'
import UserSelectBox from '@/components/searchSelectBoxes/UserSelectBox.vue';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import Pagination from '@/components/shared/Pagination.vue';

export default {
  components: {
    CustomerSelectBox,
    BuildingTypeSelectBox,
    UserSelectBox,
    SearchPanelWrapper,
    Pagination
  },
  setup() {
    const title = ref('建物一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const route = useRoute();
    const router = useRouter();
    const buildings = ref([]);
    const customers = ref([]);
    const buildingTypes = ref([]);
    const users = ref([]);

    // 検索用のデータプロパティ
    const searchCustomerName = ref('');
    const searchBuildingName = ref('');
    const searchBuildingTypeName = ref('');
    const searchInchargePersonName = ref('');

    // テーブルの高さの動的な設定
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    // 検索パネルの展開状態を管理するためのリアクティブプロパティ
    const isSearchExpanded = ref(true);

    // ▼▼ 共通：検索条件をそのまま query として返す ▼▼
    const getSearchQuery = () => ({
      annual_building_customer_id: searchCustomerName.value,
      annual_building_type_id: searchBuildingTypeName.value,
      annual_building_name: searchBuildingName.value,
      annual_building_incharge_person_id: searchInchargePersonName.value,
      annual_building_page: currentPage.value || 1
    });

    // ページネーション
    const currentPage = ref(1);
    const totalPages = ref(20); // トータルページ数を管理するためのプロパティ

    const groupedBuildings = computed(() => {
      const groups = {};
      const buildingList = Array.isArray(buildings.value) ? buildings.value : [];
      buildingList.forEach(b => {
        const cname = b.m_customer?.customer_nm || '（顧客不明）';
        if (!groups[cname]) groups[cname] = [];
        groups[cname].push(b);
      });
      return groups;
    });

    const fetchBuildings = async (page = 1) => {
      try {
        const response = await getAnnualScheduleBuildings({
          customer_id: searchCustomerName.value,
          building_type_id: searchBuildingTypeName.value,
          building_nm: searchBuildingName.value,
          incharge_person_id: searchInchargePersonName.value,
          page: page
        });
        buildings.value = Array.isArray(response.data.buildings) ? response.data.buildings : [];
        totalPages.value = response.data.meta?.total_pages || 1;
        currentPage.value = response.data.meta?.current_page || 1;
      } catch (error) {
        console.error('建物情報ーデータの取得に失敗しました:', error);
      }

      // 顧客情報（検索用）
      try {
        const response = await getSelectBoxCustomers();
        customers.value = response.data;
      } catch (error) {
        console.error('顧客情報ーデータの取得に失敗しました:', error);
      }

      // 建物種別情報（検索用）
      try {
        const response = await getBuildingTypes();
        buildingTypes.value = response.data.building_types;
      } catch (error) {
        console.error('建物種別情報ーデータの取得に失敗しました:', error);
      }

      // ユーザ情報（検索用）
      try {
        const response = await getSelectBoxUsers();
        users.value = response.data;
      } catch (error) {
        console.error('ユーザ情報ーデータの取得に失敗しました:', error);
      }
    };

    const handleChangePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        fetchBuildings(page);

        const q = getSearchQuery();
        q.annual_building_page = page;

        router.replace({
          name: 'CustomerBuildingList',
          query: q
        });

        // スクロールを tableWrapper のトップへ
        if (tableWrapper.value) {
          tableWrapper.value.scrollTop = 0;
        }
      }
    };

    // 年度別の取得関数
    const prevFiscal = (date) => getFiscalYearRange(date, -1);
    const currentFiscal = (date) => getFiscalYearRange(date, 0);
    const nextFiscal = (date) => getFiscalYearRange(date, 1);

    // 汎用の年度範囲関数（共通ロジック）
    const getFiscalYearRange = (contractDate, offset = 0) => {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      const contractMonth = contractDate ? new Date(contractDate).getMonth() + 1 : 1;

      const startYear = contractMonth <= currentMonth ? currentYear + offset : currentYear + offset - 1;

      const startMonth = new Date(startYear, contractMonth - 1);
      const endMonth = new Date(startYear + 1, contractMonth - 2); // 翌年の契約月の前月

      return {
        start: `${startMonth.getFullYear()}-${startMonth.getMonth() + 1}-${startMonth.getDate()}`,
        end: endMonth,
        label: `${startMonth.getFullYear()}年${startMonth.getMonth() + 1}月〜${endMonth.getFullYear()}年${endMonth.getMonth() + 1}月`
      };
    };

    // 建物を検索するメソッド
    const searchBuildings = async () => {
      await fetchBuildings(1);

      const q = getSearchQuery();
      q.annual_building_page = 1;

      router.push({
        name: 'CustomerBuildingList',
        query: q
      });
    };

    // 建物を検索リセットするメソッド
    const searchResetBuildings = async () => {
      searchCustomerName.value = '';
      searchBuildingName.value = '';
      searchBuildingTypeName.value = '';
      searchInchargePersonName.value = '';

      await fetchBuildings(1);

      const q = getSearchQuery();
      q.annual_building_page = 1;

      router.replace({
        name: 'CustomerBuildingList',
        query: q
      });
    };

    // ▼▼▼ クエリパラメータから検索条件を復元 ▼▼▼
    onMounted(() => {
      const q = route.query;

      searchCustomerName.value = q.annual_building_customer_id || '';
      searchBuildingName.value = q.annual_building_name || '';
      searchBuildingTypeName.value = q.annual_building_type_id || '';
      searchInchargePersonName.value = q.annual_building_incharge_person_id || '';
      currentPage.value = q.annual_building_page ? Number(q.annual_building_page) : 1;

      fetchBuildings(currentPage.value);
    });

    return {
      title,
      route,
      buildings,
      customers,
      buildingTypes,
      users,
      getSearchQuery,
      searchCustomerName,
      searchBuildingName,
      searchBuildingTypeName,
      searchInchargePersonName,
      groupedBuildings,
      currentPage,
      totalPages,
      handleChangePage,
      prevFiscal,
      currentFiscal,
      nextFiscal,
      searchBuildings,
      searchResetBuildings,
      isSearchExpanded,
      topFixArea,
      tableWrapper,
      paginationContainer
    };
  }
};
</script>

<style scoped>
.building-item {
  border: 1px solid #ddd;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.building-item:hover {
  background-color: #f5f5f5;
}

table th,
table td{
  font-size: 14px;
}

table .btn-cell{
  width: 204px;
  min-width: 204px;
  max-width: 204px;
}
</style>
